import { useBrandFontFamilies } from '@src/hooks/useBrandFontFamilies';
import { useHoldRepeat } from '@src/hooks/useHoldRepeat';
import { useFeatureFlags } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { Bold, BoldValues } from '@src/utility/fonts';
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  FONTS,
  JAPANESE_FONTS,
} from '@src/utility/general';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import BoldIcon from '../../svgs/BoldIcon';
import CapitalizeIcon from '../../svgs/CapitalizationIcon';
import ItalicIcon from '../../svgs/ItalicIcon';
import LeftAlignIcon from '../../svgs/LeftAlignIcon';
import MiddleAlignIcon from '../../svgs/MiddleAlignIcon';
import RightAlignIcon from '../../svgs/RightAlignIcon';
import TextColorIcon from '../../svgs/TextColorIcon';
import ColorPicker from './ColorPicker';

import type { FontFamily } from '@src/types.ts/story';

const TOOLBAR_HEIGHT = 44;
// Vertical offset applied to the toolbar's natural position. Positive values
// push the toolbar lower on screen (toward / over the canvas). Bumped to 40 px
// so the toolbar no longer hovers tight against the canvas's top edge.
const TOOLBAR_Y_OFFSET = 40;

// Font-size bounds for the stepper — shared with the settings-panel Font Size
// fields so both surfaces clamp identically. Outside these, the user has to
// type a value directly in the panel.
const MIN_FONT_SIZE = FONT_SIZE_MIN;
const MAX_FONT_SIZE = FONT_SIZE_MAX;

const TextFloatingToolbar = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const fontPickerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [fontPickerPos, setFontPickerPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [colorPickerRect, setColorPickerRect] = useState<DOMRect | null>(null);
  const [fontSearch, setFontSearch] = useState('');

  const activeElement = videoCreator.getActiveElement();
  // Hidden during playback like the rest of the on-canvas editing chrome. The
  // selection now survives play (PLAT-11402), so the toolbar has to opt out.
  const isTextEl =
    !videoCreator.isPlaying &&
    activeElement?.source?.type === 'text' &&
    activeElement.track !== KARAOKE_TRACK_NUMBER;

  // Sync textProducer config when element changes
  useEffect(() => {
    if (isTextEl && activeElement?.source) {
      videoCreator.textProducer.setTextConfig(activeElement.source);
    }
  }, [isTextEl, activeElement?.source?.id]);

  // Recompute toolbar position — no ref guard: toolbar is always mounted when isTextEl
  const computePos = () => {
    const canvasEl = videoCreator.renderer?.element;
    if (!canvasEl) return;
    const cb = canvasEl.getBoundingClientRect();
    const tw = toolbarRef.current?.offsetWidth ?? 0;
    const x = Math.max(8, cb.left + (cb.width - tw) / 2);
    // Try to sit just above the canvas; fall back to 8px below canvas top.
    // Both anchors shift down by TOOLBAR_Y_OFFSET so the toolbar doesn't
    // hover tight against the canvas's edge.
    const yAbove = cb.top - TOOLBAR_HEIGHT - 8 + TOOLBAR_Y_OFFSET;
    const y = yAbove >= 8 ? yAbove : cb.top + 8 + TOOLBAR_Y_OFFSET;
    setPos({ x, y });
  };

  useLayoutEffect(() => {
    if (!isTextEl) {
      setPos(null);
      return;
    }
    computePos();
  }, [isTextEl, activeElement?.source?.id]);

  // Keep position fresh on resize / scroll
  useEffect(() => {
    if (!isTextEl) return;
    const ro = new ResizeObserver(computePos);
    const canvasEl = videoCreator.renderer?.element;
    if (canvasEl) ro.observe(canvasEl);
    window.addEventListener('resize', computePos);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', computePos);
    };
  }, [isTextEl, videoCreator.renderer?.element]);

  // Close font picker on outside click
  useEffect(() => {
    if (!fontPickerPos) return;
    const handler = (e: MouseEvent) => {
      if (!fontPickerRef.current?.contains(e.target as Node)) {
        setFontPickerPos(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [fontPickerPos]);

  // Close font picker when element changes
  useEffect(() => {
    setFontPickerPos(null);
  }, [activeElement?.source?.id]);

  // Reset the font search whenever the picker closes so it reopens clean.
  useEffect(() => {
    if (!fontPickerPos) setFontSearch('');
  }, [fontPickerPos]);

  const fontFamilies = useBrandFontFamilies();

  // When the "Hide Arbor Fonts" flag is on, only brand/custom fonts are
  // offered — the curated popular (Arbor) font list is suppressed entirely.
  const { creatorStudioFontsHideArborFonts } = useFeatureFlags();

  const customFamilyNames = useMemo(
    () => new Set(fontFamilies.map(f => f.name)),
    [fontFamilies],
  );
  // The floating toolbar only targets free-form text (it excludes the karaoke
  // track above), so the Japanese fonts are always offered — not gated on the
  // transcript language the way captions are (PLAT-10420).
  const popularFonts = useMemo(() => {
    if (creatorStudioFontsHideArborFonts) return [];
    return [...FONTS, ...JAPANESE_FONTS].filter(
      name => !customFamilyNames.has(name),
    );
  }, [customFamilyNames, creatorStudioFontsHideArborFonts]);

  // Case-insensitive substring filter on the family name (mirrors
  // FontFamilyVariantDropdown's search).
  const fontQuery = fontSearch.trim().toLowerCase();
  const filteredBrandFamilies = useMemo(
    () =>
      fontQuery
        ? fontFamilies.filter(f => f.name.toLowerCase().includes(fontQuery))
        : fontFamilies,
    [fontQuery, fontFamilies],
  );
  const filteredPopularFonts = useMemo(
    () =>
      fontQuery
        ? popularFonts.filter(n => n.toLowerCase().includes(fontQuery))
        : popularFonts,
    [fontQuery, popularFonts],
  );

  const config = videoCreator.textProducer.getTextConfig();
  const currentVariantStyle = useMemo(() => {
    const ps = config?.font_family as string | undefined;
    if (!ps) return null;
    for (const f of fontFamilies) {
      const v = f.variants.find(v => v.postScriptName === ps);
      if (v) return v.style;
    }
    return null;
  }, [config?.font_family, fontFamilies]);

  // Stepper press-and-hold hooks. MUST sit above the early return below so
  // hook order is stable across renders (React doesn't permit conditional
  // hook calls).
  const holdDec = useHoldRepeat();
  const holdInc = useHoldRepeat();

  if (!isTextEl) return null;

  const fontFamily = (config?.font_family || 'Inter').split(',')[0].trim();
  const fontSize = Math.round(config?.font_size || 32);
  const fillColor =
    typeof config?.fill_color === 'string' ? config.fill_color : '#ffffff';
  // 600+ counts as "bold" here so the button reflects the actually-applied
  // variant weight after a click. The variant-find logic below also uses
  // ≥600 as the band threshold — keep both in sync, otherwise a SemiBold
  // swap (weight 600) would leave the button visually OFF.
  const isBold = Number(config?.font_weight) >= 600;
  const isItalic =
    currentVariantStyle === 'italic' || config?.font_style === 'italic';
  const isCapitalized = config?.text_transform === 'uppercase';
  const alignment = (config?.x_alignment as string) || '0%';

  // ─── Canva-style B/I synthesis tracking ─────────────────────────────────
  // Buttons are always clickable. When a real variant of the current
  // family exists, we swap font_family to that variant; when none exists,
  // we fall back to CSS-level font_weight / font_style so the click still
  // produces a visible change, with a small amber dot on the button to
  // signal "synthetic — final render may differ".
  const currentPostScript = config?.font_family as string | undefined;
  const currentFamily = fontFamilies.find(f =>
    f.variants.some(v => v.postScriptName === currentPostScript),
  );
  const currentVariant = currentFamily?.variants.find(
    v => v.postScriptName === currentPostScript,
  );
  const currentVariantWeight =
    currentVariant?.weight ?? (Number(config?.font_weight) || 400);
  const currentTrackStyle: 'normal' | 'italic' =
    currentVariant?.style ??
    (config?.font_style === 'italic' ? 'italic' : 'normal');
  // Popular Google Fonts (curated FONTS list) ship full weight/italic axes,
  // so we trust them — no synth indicator there.
  const isPopularFont = !currentFamily;
  // "Synthetic" = the style is applied but the rendered font face isn't
  // actually that style/weight, so the renderer has to approximate via
  // CSS-level font-weight/font-style. We mark it for brand-kit fonts only;
  // Google Fonts via the curated list are trusted.
  const isBoldSynthetic =
    isBold && !isPopularFont && currentVariantWeight < 600;
  const isItalicSynthetic =
    isItalic && !isPopularFont && currentVariantStyle !== 'italic';

  const modify = (patch: Record<string, unknown>) => {
    // font_size is stored as display px in config but Creatomate needs vh
    const expanded: Record<string, unknown> = { ...patch };
    if ('font_size' in expanded) {
      expanded.font_size = videoCreator.textProducer.convertToVh(
        String(expanded.font_size),
        activeElement?.source?.id,
      );
    }
    const editorStore = videoCreator.rootStore.editorStore;
    if (editorStore?.editor && activeElement?.source?.id) {
      editorStore.editor.commandUpdateElement(
        activeElement.source.id,
        expanded,
      );
      videoCreator.textProducer.modifyProperty(patch, false, null);
      // modifyProperty snaps font_size to FONT_SIZE_VALUES via findClosestValue,
      // making the stepper appear frozen. Override with the raw px value so the
      // displayed number matches what was actually sent to the renderer.
      if ('font_size' in patch) {
        runInAction(() => {
          videoCreator.textProducer.config = {
            ...videoCreator.textProducer.config,
            font_size: Number(patch.font_size),
          };
        });
      }
    } else {
      videoCreator.textProducer.modifyProperty(patch);
    }
  };

  // ─── Stepper press-and-hold + bounds ────────────────────────────────────
  // `holdDec` / `holdInc` are declared above the early return (hooks). Here
  // we just compute the bound flags + the action closures. Read the latest
  // font_size on each call so the interval keeps moving forward (closing
  // over `fontSize` would freeze the action at the hold-start value).
  const canDecrement = fontSize > MIN_FONT_SIZE;
  const canIncrement = fontSize < MAX_FONT_SIZE;
  const stepDown = () => {
    const cfg = videoCreator.textProducer.getTextConfig();
    const current = Math.round(cfg?.font_size || 32);
    if (current <= MIN_FONT_SIZE) return;
    modify({ font_size: Math.max(MIN_FONT_SIZE, current - 1) });
  };
  const stepUp = () => {
    const cfg = videoCreator.textProducer.getTextConfig();
    const current = Math.round(cfg?.font_size || 32);
    if (current >= MAX_FONT_SIZE) return;
    modify({ font_size: Math.min(MAX_FONT_SIZE, current + 1) });
  };

  const handleSelectFont = (name: string, family: FontFamily | undefined) => {
    if (family) {
      const currentWeight = Number(config?.font_weight) || 400;
      const active = family.variants.filter(v => !v.archivedAt);
      const best = active.sort(
        (a, b) =>
          Math.abs(a.weight - currentWeight) -
          Math.abs(b.weight - currentWeight),
      )[0];
      if (best)
        modify({
          font_family: best.postScriptName,
          font_weight: best.weight.toString(),
        });
    } else {
      modify({ font_family: name });
    }
    setFontPickerPos(null);
  };

  const toolbarStyle = pos
    ? { left: pos.x, top: pos.y }
    : { left: -9999, top: -9999, visibility: 'hidden' as const };

  const toolbar = (
    <SToolbar ref={toolbarRef} style={toolbarStyle}>
      {/* Font family */}
      <SFontName
        title={fontFamily}
        isOpen={!!fontPickerPos}
        onClick={e => {
          if (fontPickerPos) {
            setFontPickerPos(null);
          } else {
            const r = e.currentTarget.getBoundingClientRect();
            setFontPickerPos({ top: r.bottom + 6, left: r.left });
          }
        }}
      >
        {fontFamily}
      </SFontName>

      <SDivider />

      {/* Size stepper — click for ±1, hold to repeat. Bounded to
       * [MIN_FONT_SIZE, MAX_FONT_SIZE]; buttons go disabled when the bound
       * is hit so the user has a clear visual stop. Wired on mouse + touch
       * + keyboard so all input methods feel the same. */}
      <SStepper>
        <SStepBtn
          disabled={!canDecrement}
          aria-label="Decrease font size"
          onMouseDown={() => canDecrement && holdDec.start(stepDown)}
          onMouseUp={holdDec.stop}
          onMouseLeave={holdDec.stop}
          onTouchStart={e => {
            if (!canDecrement) return;
            // Prevent the synthesized mousedown that would otherwise
            // double-fire the action immediately after touchstart.
            e.preventDefault();
            holdDec.start(stepDown);
          }}
          onTouchEnd={holdDec.stop}
          onTouchCancel={holdDec.stop}
          onKeyDown={e => {
            if (
              canDecrement &&
              (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')
            ) {
              e.preventDefault();
              stepDown();
            }
          }}
        >
          −
        </SStepBtn>
        <SStepValue>{fontSize}</SStepValue>
        <SStepBtn
          disabled={!canIncrement}
          aria-label="Increase font size"
          onMouseDown={() => canIncrement && holdInc.start(stepUp)}
          onMouseUp={holdInc.stop}
          onMouseLeave={holdInc.stop}
          onTouchStart={e => {
            if (!canIncrement) return;
            e.preventDefault();
            holdInc.start(stepUp);
          }}
          onTouchEnd={holdInc.stop}
          onTouchCancel={holdInc.stop}
          onKeyDown={e => {
            if (
              canIncrement &&
              (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowUp')
            ) {
              e.preventDefault();
              stepUp();
            }
          }}
        >
          +
        </SStepBtn>
      </SStepper>

      <SDivider />

      {/* Color swatch */}
      <SColorBtn
        title="Text color"
        isOpen={!!colorPickerRect}
        onClick={e => {
          if (colorPickerRect) {
            setColorPickerRect(null);
          } else {
            setColorPickerRect(e.currentTarget.getBoundingClientRect());
          }
        }}
      >
        <TextColorIcon />
      </SColorBtn>
      {/* ColorPicker rendered via separate portal — see return below */}

      <SDivider />

      {/* B / I / U — Canva-style: always interactive. Swap to a real
       * variant when one exists in the current family; fall back to CSS
       * font_weight / font_style otherwise. A small amber dot signals
       * "synthetic — final render may differ" without disabling the
       * button (the user can still apply the style and decide what to do). */}
      <SIconBtn
        isActive={isBold}
        title={
          isBoldSynthetic
            ? `Synthetic bold — no bold variant in ${currentFamily?.name ?? 'this font'}. Upload one in Brand Kit Fonts for a true bold.`
            : 'Bold'
        }
        onClick={() => {
          const targetBold = !isBold;
          // Brand-kit path: snap to a real variant of the current style
          // track (normal/italic) at the target weight band.
          if (currentFamily) {
            const isInBand = targetBold
              ? (w: number) => w >= 600
              : (w: number) => w < 600;
            const realMatch = currentFamily.variants.find(
              v =>
                !v.archivedAt &&
                isInBand(v.weight) &&
                v.style === currentTrackStyle,
            );
            if (realMatch) {
              modify({
                font_family: realMatch.postScriptName,
                font_weight: realMatch.weight.toString(),
              });
              return;
            }
          }
          // Synthesis fallback — keep the current font_family, CSS handles it.
          modify({
            font_weight: targetBold
              ? '700'
              : BoldValues[Bold.normal].toString(),
          });
        }}
        aria-label="Bold"
        aria-pressed={isBold}
      >
        <BoldIcon
          fillColor={isBold ? 'var(--foreground)' : 'var(--text-muted)'}
        />
        {isBoldSynthetic && <SSynthDot aria-hidden />}
      </SIconBtn>

      <SIconBtn
        isActive={isItalic}
        title={
          isItalicSynthetic
            ? `Synthetic italic — no italic variant in ${currentFamily?.name ?? 'this font'}. Upload one in Brand Kit Fonts for a true italic.`
            : 'Italic'
        }
        onClick={() => {
          const targetStyle: 'normal' | 'italic' = isItalic
            ? 'normal'
            : 'italic';
          // Brand-kit path: find a same-weight (or closest) variant in the
          // target style track.
          if (currentFamily) {
            const candidates = currentFamily.variants.filter(
              v => !v.archivedAt && v.style === targetStyle,
            );
            if (candidates.length > 0) {
              const best = [...candidates].sort(
                (a, b) =>
                  Math.abs(a.weight - currentVariantWeight) -
                  Math.abs(b.weight - currentVariantWeight),
              )[0];
              modify({
                font_family: best.postScriptName,
                font_weight: best.weight.toString(),
                font_style: targetStyle,
              });
              return;
            }
          }
          // Synthesis fallback — CSS font_style only.
          modify({ font_style: targetStyle });
        }}
        aria-label="Italic"
        aria-pressed={isItalic}
      >
        <ItalicIcon
          fillColor={isItalic ? 'var(--foreground)' : 'var(--text-muted)'}
        />
        {isItalicSynthetic && <SSynthDot aria-hidden />}
      </SIconBtn>

      {/* Underline button intentionally omitted — Creatomate's text
       * renderer has no underline / text-decoration property, so the
       * button could never actually underline the text. See the spike
       * trace in PLAT-9972's history (commits 4840ddf18 / 5599e5f64)
       * for the U+0332 workaround that was tried and rolled back. */}

      <SDivider />

      {/* Case */}
      <SIconBtn
        isActive={isCapitalized}
        onClick={() =>
          modify({
            text_transform: isCapitalized ? 'none' : 'uppercase',
          })
        }
        aria-label="Uppercase"
        aria-pressed={isCapitalized}
      >
        <CapitalizeIcon
          fillColor={isCapitalized ? 'var(--foreground)' : 'var(--text-muted)'}
        />
      </SIconBtn>

      <SDivider />

      {/* Alignment */}
      <SIconBtn
        isActive={alignment === '0%'}
        onClick={() => modify({ x_alignment: '0%' })}
        aria-label="Align left"
        aria-pressed={alignment === '0%'}
      >
        <LeftAlignIcon
          fillColor={
            alignment === '0%' ? 'var(--foreground)' : 'var(--text-muted)'
          }
        />
      </SIconBtn>
      <SIconBtn
        isActive={alignment === '50%'}
        onClick={() => modify({ x_alignment: '50%' })}
        aria-label="Align center"
        aria-pressed={alignment === '50%'}
      >
        <MiddleAlignIcon
          strokeColor={
            alignment === '50%' ? 'var(--foreground)' : 'var(--text-muted)'
          }
        />
      </SIconBtn>
      <SIconBtn
        isActive={alignment === '100%'}
        onClick={() => modify({ x_alignment: '100%' })}
        aria-label="Align right"
        aria-pressed={alignment === '100%'}
      >
        <RightAlignIcon
          fillColor={
            alignment === '100%' ? 'var(--foreground)' : 'var(--text-muted)'
          }
        />
      </SIconBtn>
    </SToolbar>
  );

  // A sibling of the toolbar, never a child: SToolbar's fade-in ends at
  // transform: scale(1), which would make it the containing block for a
  // position: fixed descendant.
  const fontPicker = fontPickerPos ? (
    <SFontPicker
      ref={fontPickerRef}
      style={{ top: fontPickerPos.top, left: fontPickerPos.left }}
    >
      <SFontSearchInput
        autoFocus
        placeholder="Search"
        value={fontSearch}
        onChange={e => setFontSearch(e.target.value)}
      />
      <SFontList>
        {filteredBrandFamilies.length > 0 && (
          <>
            <SFontSection>Brand Fonts</SFontSection>
            {filteredBrandFamilies.map(f => {
              const isSelected = f.variants.some(
                v => v.postScriptName === config?.font_family,
              );
              return (
                <SFontOption
                  key={f.name}
                  fontFamily={f.name}
                  isSelected={isSelected}
                  onClick={() => handleSelectFont(f.name, f)}
                >
                  {f.name}
                </SFontOption>
              );
            })}
            {filteredPopularFonts.length > 0 && <SFontDivider />}
          </>
        )}
        {filteredPopularFonts.length > 0 && (
          <>
            <SFontSection>Popular Fonts</SFontSection>
            {filteredPopularFonts.map(name => (
              <SFontOption
                key={name}
                fontFamily={name}
                isSelected={fontFamily === name}
                onClick={() => handleSelectFont(name, undefined)}
              >
                {name}
              </SFontOption>
            ))}
          </>
        )}
        {filteredBrandFamilies.length === 0 &&
          filteredPopularFonts.length === 0 && (
            <SFontEmpty>No fonts match "{fontSearch}"</SFontEmpty>
          )}
      </SFontList>
    </SFontPicker>
  ) : null;

  // ColorPicker portals itself to document.body and computes its own placement,
  // so it's safe to render inline even though SToolbar's fade-in animation has
  // transform: scale(1) (which would otherwise create a containing block that
  // traps position: fixed children).
  const colorPicker = colorPickerRect ? (
    <ColorPicker
      pos={colorPickerRect}
      defaultValue={fillColor}
      solidDefaultAlpha
      getValue={color => {
        if (color) modify({ fill_color: color });
      }}
      onClose={() => setColorPickerRect(null)}
    />
  ) : null;

  return (
    <>
      {toolbar}
      {fontPicker}
      {colorPicker}
    </>
  );
});

export default TextFloatingToolbar;

// ─── animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97) translateY(4px); }
  to   { opacity: 1; transform: scale(1)    translateY(0);   }
`;

// ─── styled components ───────────────────────────────────────────────────────

const SToolbar = styled.div`
  position: fixed;
  /* Deliberately small. The toolbar renders inside Stage's Main rather than a
     body portal, so this is ranked against the stage's own layers: above the
     canvas, below FitDropdown (10000) and AspectRatiosDropdown (1000008) so
     those open over it. Raising it here puts the toolbar back on top of them. */
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 2px;
  height: ${TOOLBAR_HEIGHT}px;
  padding: 0 10px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.35),
    0 1px 3px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 140ms ease-out both;
  pointer-events: all;
  user-select: none;
  white-space: nowrap;
`;

const SDivider = styled.div`
  width: 1px;
  height: 20px;
  background: var(--border);
  margin: 0 4px;
  flex-shrink: 0;
`;

const SFontName = styled.button<{ isOpen: boolean }>`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  background: ${({ isOpen }) => (isOpen ? 'var(--surface-elevated)' : 'none')};
  border: none;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    background: var(--surface-elevated);
  }
`;

const SFontPicker = styled.div`
  position: fixed;
  /* One above SToolbar, in the same stage-local scale. */
  z-index: 101;
  width: 200px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 6px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 2px 6px rgba(0, 0, 0, 0.2);
`;

const SFontSearchInput = styled.input`
  /* border-box so width:100% accounts for the input's own padding + border
   * and the right edge stays inside the picker's rounded corner. */
  box-sizing: border-box;
  width: 100%;
  height: 32px;
  min-height: 32px;
  flex-shrink: 0;
  padding: 0 10px;
  margin-bottom: 6px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: var(--accent);
  }
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SFontList = styled.div`
  /* The search field above keeps its height; this list absorbs the clamp
   * and scrolls instead. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 2px;
  }
`;

const SFontEmpty = styled.div`
  padding: 10px;
  color: var(--text-muted);
  font-size: var(--text-sm);
  text-align: center;
`;

const SFontSection = styled.span`
  display: block;
  padding: 6px 6px 2px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
  letter-spacing: 0.4px;
`;

const SFontDivider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 4px 0;
`;

const SFontOption = styled.button<{ fontFamily: string; isSelected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: ${({ isSelected }) =>
    isSelected ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'none'};
  color: ${({ isSelected }) =>
    isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
  font-family: ${({ fontFamily }) => fontFamily}, Inter;
  font-size: var(--text-sm);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    background: ${({ isSelected }) =>
      isSelected
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

const SStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const SStepBtn = styled.button`
  width: 24px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  user-select: none;
  -webkit-touch-callout: none;
  /* Prevent the browser from interpreting a long-press on touch as a text
   * selection / context-menu gesture while the user is holding to repeat. */
  touch-action: manipulation;
  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  &:active:not(:disabled) {
    background: var(--border);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    color: var(--text-muted);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

const SStepValue = styled.span`
  min-width: 28px;
  text-align: center;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  padding: 0 2px;
`;

const SColorBtn = styled.button<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${({ isOpen }) => (isOpen ? 'var(--surface-elevated)' : 'none')};
  border: 1px solid
    ${({ isOpen }) => (isOpen ? 'var(--border)' : 'transparent')};
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  &:hover {
    background: var(--surface-elevated);
  }
`;

const SIconBtn = styled.button<{ isActive: boolean }>`
  position: relative;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isActive }) =>
    isActive ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'none'};
  border: ${({ isActive }) =>
    isActive ? '1px solid var(--accent)' : '1px solid transparent'};
  border-radius: 6px;
  cursor: pointer;
  transition:
    background 100ms ease,
    border-color 100ms ease;
  &:hover {
    background: ${({ isActive }) =>
      isActive
        ? 'color-mix(in srgb, var(--accent) 22%, transparent)'
        : 'var(--surface-elevated)'};
  }
  &:active {
    background: var(--border);
  }
  /* Suppress the browser's default outline on mouse-click focus — the
   * isActive state already communicates 'this is on' via background +
   * border. Keep the focus ring for keyboard users via focus-visible. */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

/**
 * Small amber dot anchored to the top-right of an SIconBtn. Signals that
 * the applied B/I style is synthesised via CSS (no real variant exists in
 * the current font family) — so the in-app preview may approximate the
 * effect, and the final rendered video may not honour it. Compact,
 * non-blocking, mirrors Canva's quiet "approximated styling" indicator.
 */
const SSynthDot = styled.span`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--warning, #f59e0b);
  pointer-events: none;
`;
